"use client";

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { DoOrDumpDashboardPayload } from "@/types/do-or-dump-dashboard";
import { formatDashboardCategoryLane } from "@/data/product-categories";
import { buildMonthlyProjections } from "@/lib/do-or-dump-projections";
import { DO_OR_DUMP_UPSELL_BUSINESS_PUSH, DO_OR_DUMP_UPSELL_NICHE } from "@/lib/do-or-dump-upsells";
import { NarrativeSkeleton } from "@/components/dashboard/NarrativeSkeleton";
import { useDoOrDumpReportNarrative } from "@/components/dashboard/use-do-or-dump-report-narrative";
import { ProjectionChart } from "@/components/dashboard/ProjectionChart";
import {
  audienceTierShade,
  insightRatingTextClasses,
  momentumShade,
  timeTierShade,
  unitsCeilingTileValue,
  volumeGoalVsCeilingShade,
  type InsightShade,
} from "@/lib/dashboard-verdict-insights";
import { invokeEdgeFunction } from "@/lib/edge-functions";

function VerdictMetricTile({
  label,
  shade,
  children,
}: {
  label: string;
  shade: InsightShade;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5">
      <p className="text-[10px] font-semibold uppercase leading-none tracking-wide text-zinc-500">{label}</p>
      <div className={`mt-0.5 text-xs font-bold leading-none ${insightRatingTextClasses(shade)}`}>{children}</div>
    </div>
  );
}

function Quadrant({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex h-full flex-col rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-black tracking-tight text-zinc-900">{title}</h3>
      {subtitle ? <p className="mt-1 text-xs leading-relaxed text-zinc-500">{subtitle}</p> : null}
      <div className="mt-4 flex-1 text-sm leading-relaxed text-zinc-700">{children}</div>
    </section>
  );
}

export type DoOrDumpDashboardPreviewReason =
  | "sign_in"
  | "no_submission"
  | "server_config"
  | "load_error";

export function DoOrDumpReportDashboard({
  payload,
  preview,
  previewReason,
}: {
  payload: DoOrDumpDashboardPayload;
  preview?: boolean;
  previewReason?: DoOrDumpDashboardPreviewReason;
}) {
  const projections = useMemo(
    () =>
      buildMonthlyProjections({
        ordersPerMonth: payload.ordersPerMonth,
        price: payload.price,
        cost: payload.cost,
        capacity: payload.capacity,
      }),
    [payload.ordersPerMonth, payload.price, payload.cost, payload.capacity],
  );

  const categoryLane = useMemo(
    () => formatDashboardCategoryLane(payload.productPrimary, payload.productSub),
    [payload.productPrimary, payload.productSub],
  );

  const unitsCeilingLabel = unitsCeilingTileValue(payload);
  const volumeShade = volumeGoalVsCeilingShade(payload.unitsForGoal, payload.capacity, payload.profitPerItem);

  const { narrative, loading: narrativeLoading } = useDoOrDumpReportNarrative(payload, { enabled: !preview });

  const [upsellNiche, setUpsellNiche] = useState(false);
  const [upsellBusinessPush, setUpsellBusinessPush] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  const upsellTotal =
    (upsellNiche ? DO_OR_DUMP_UPSELL_NICHE.amountUsd : 0) +
    (upsellBusinessPush ? DO_OR_DUMP_UPSELL_BUSINESS_PUSH.amountUsd : 0);

  async function handleCheckout() {
    setCheckoutError("");
    if (upsellTotal <= 0) return;
    setCheckoutLoading(true);
    try {
      const data = await invokeEdgeFunction<{ url?: string; error?: string }>("stripe-checkout", {
        email: payload.email,
        upsellNiche,
        upsellPush: upsellBusinessPush,
        origin: window.location.origin,
      });
      if (data && typeof data === "object" && "error" in data && data.error) {
        setCheckoutError(data.error);
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch {
      setCheckoutError("Network error.");
    } finally {
      setCheckoutLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 pb-16 pt-8 text-zinc-900 sm:px-6 lg:px-8">
      {preview ? (
        <p className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          {previewReason === "sign_in" ? (
            <>
              You&apos;re viewing a sample layout. <strong>Open the confirmation link from your email</strong> after you run Do or Dump — your verdict and numbers live here once you&apos;re signed in (no disposable report links).
            </>
          ) : previewReason === "no_submission" ? (
            <>
              Signed in as <strong>{payload.email}</strong>, but we don&apos;t have a saved questionnaire yet.{" "}
              <Link to="/do-or-dump" className="font-semibold text-violet-800 underline-offset-2 hover:underline">
                Run Do or Dump
              </Link>{" "}
              to attach a report to this account.
            </>
          ) : previewReason === "server_config" || previewReason === "load_error" ? (
            <>
              We couldn&apos;t load your report right now. You&apos;re seeing a sample — please try again in a little while.
            </>
          ) : (
            <>You&apos;re viewing a sample report.</>
          )}
        </p>
      ) : null}

      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-600">Your report</p>
        <h1 className="mt-1 text-3xl font-black tracking-tight sm:text-4xl">Do or Dump</h1>
        <p className="mt-2 max-w-2xl text-sm text-zinc-600">{categoryLane}</p>
      </header>

      {/* Verdict strip */}
      <div className="mb-8 grid gap-4 lg:grid-cols-3">
        <div
          className={`flex flex-col justify-center rounded-2xl px-6 py-8 text-center ring-2 lg:col-span-1 ${payload.badgeClass}`}
        >
          <p className="text-xs font-bold uppercase tracking-widest opacity-90">Verdict</p>
          <p className="mt-2 text-4xl font-black sm:text-5xl">{payload.verdict}</p>
          <p className="mt-3 text-sm font-medium leading-snug opacity-95">{payload.verdictLine}</p>
        </div>
        <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm lg:col-span-2">
          <h2 className="text-sm font-bold text-zinc-900">Overview</h2>
          <div className="mt-3">
            {narrativeLoading ? (
              <NarrativeSkeleton />
            ) : narrative ? (
              <p className="whitespace-pre-line text-sm leading-relaxed text-zinc-700">{narrative.overview}</p>
            ) : preview ? (
              <p className="text-sm text-zinc-500">
                The AI-written overview appears on your personal report after you complete Do or Dump — not on this sample layout.
              </p>
            ) : (
              <p className="text-sm text-zinc-500">No narrative yet.</p>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
            <VerdictMetricTile label="Momentum" shade={momentumShade(payload.momentum)}>
              <p className="font-bold tabular-nums">{payload.momentum}/100</p>
            </VerdictMetricTile>

            <VerdictMetricTile label="Units to sell" shade={volumeShade}>
              <p className="font-bold tabular-nums">{unitsCeilingLabel}</p>
            </VerdictMetricTile>

            <VerdictMetricTile label="Time band" shade={timeTierShade(payload.timeTier)}>
              <p className="font-bold capitalize">{payload.timeTier}</p>
            </VerdictMetricTile>

            <VerdictMetricTile label="Social media reach" shade={audienceTierShade(payload.audienceTier)}>
              <p className="font-bold capitalize">{payload.audienceTier}</p>
            </VerdictMetricTile>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <ProjectionChart points={projections} />
      </div>

      <div className="mb-6 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-bold text-zinc-900">Your inputs vs model reality</h2>
        <div className="mt-3">
          {narrativeLoading ? (
            <NarrativeSkeleton />
          ) : narrative ? (
            <p className="whitespace-pre-line text-sm leading-relaxed text-zinc-700">{narrative.inputsVsModel}</p>
          ) : (
            <p className="text-sm text-zinc-500">—</p>
          )}
        </div>
        <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Price" value={`$${payload.price.toFixed(0)}`} />
          <Stat label="Cost" value={`$${payload.cost.toFixed(0)}`} />
          <Stat label="Profit / unit" value={`$${payload.profitPerItem.toFixed(0)}`} />
          <Stat label="Goal / mo" value={`$${payload.goalMonthly.toLocaleString()}`} />
          <Stat label="Investment budget (stated)" value={`$${payload.investmentBudgetUsd.toLocaleString()}`} />
          <Stat label="Hours / week" value={`${payload.hoursPerWeek}`} />
          <Stat label="Discovery" value={payload.acquisitionsLabel} wide />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Quadrant title="Competitor corner">
          {narrativeLoading ? (
            <NarrativeSkeleton />
          ) : narrative ? (
            <ul className="space-y-4">
              <li>
                <p className="text-xs font-bold uppercase tracking-wide text-violet-600">High end</p>
                <p className="mt-1 whitespace-pre-line">{narrative.competitorHighEnd}</p>
              </li>
              <li>
                <p className="text-xs font-bold uppercase tracking-wide text-violet-600">Middle</p>
                <p className="mt-1 whitespace-pre-line">{narrative.competitorMiddle}</p>
              </li>
              <li>
                <p className="text-xs font-bold uppercase tracking-wide text-violet-600">Peer / new entrant</p>
                <p className="mt-1 whitespace-pre-line">{narrative.competitorPeer}</p>
              </li>
            </ul>
          ) : (
            <p className="text-sm text-zinc-500">—</p>
          )}
        </Quadrant>

        <Quadrant title="Market pulse">
          {narrativeLoading ? (
            <NarrativeSkeleton />
          ) : narrative ? (
            <p className="whitespace-pre-line">{narrative.marketPulse}</p>
          ) : (
            <p className="text-sm text-zinc-500">—</p>
          )}
        </Quadrant>

        <Quadrant title="Differentiation">
          {narrativeLoading ? (
            <NarrativeSkeleton />
          ) : narrative ? (
            <p className="whitespace-pre-line">{narrative.differentiation}</p>
          ) : (
            <p className="text-sm text-zinc-500">—</p>
          )}
        </Quadrant>

        <Quadrant title="Socials & acquisition">
          {narrativeLoading ? (
            <NarrativeSkeleton />
          ) : narrative ? (
            <p className="whitespace-pre-line">{narrative.socialsAcquisition}</p>
          ) : (
            <p className="text-sm text-zinc-500">—</p>
          )}
        </Quadrant>

        <section className="flex flex-col rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm lg:col-span-2">
          <h3 className="text-base font-black tracking-tight text-zinc-900">Why you</h3>
          <div className="mt-4 text-sm leading-relaxed text-zinc-700">
            {narrativeLoading ? (
              <NarrativeSkeleton />
            ) : narrative ? (
              <p className="whitespace-pre-line">{narrative.whyYou}</p>
            ) : (
              <p className="text-zinc-500">—</p>
            )}
          </div>

          <div className="mt-8 border-t border-zinc-200 pt-8">
            <h4 className="text-sm font-bold text-zinc-900">Next steps</h4>
            <p className="mt-3 text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">One high impact tweak</p>
            <div className="mt-2 text-sm font-bold leading-relaxed text-zinc-900">
              {narrativeLoading ? (
                <NarrativeSkeleton />
              ) : narrative ? (
                <p>
                  Based on what you put in, here&apos;s what I&apos;d question first: {narrative.highImpactTweak}
                </p>
              ) : (
                <p className="font-normal text-zinc-500">—</p>
              )}
            </div>
            <p className="mt-4 text-sm leading-relaxed text-zinc-600">
              Want this translated into audit‑level notes and a roadmap? Pick one or both below — you&apos;ll complete payment on the
              next screen.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="flex cursor-pointer gap-3 rounded-xl border border-zinc-200 bg-zinc-50/80 p-3 has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50">
                <input
                  type="checkbox"
                  checked={upsellNiche}
                  onChange={(e) => setUpsellNiche(e.target.checked)}
                  className="mt-1 size-5 accent-violet-600"
                />
                <span>
                  <span className="font-bold">{DO_OR_DUMP_UPSELL_NICHE.label}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-zinc-600">{DO_OR_DUMP_UPSELL_NICHE.detail}</span>
                  <span className="mt-1 block text-xs font-bold text-zinc-900">
                    Turnaround: {DO_OR_DUMP_UPSELL_NICHE.turnaround}
                  </span>
                  <span className="mt-1 block text-sm font-black text-violet-700">${DO_OR_DUMP_UPSELL_NICHE.amountUsd.toFixed(2)}</span>
                </span>
              </label>
              <label className="flex cursor-pointer gap-3 rounded-xl border border-zinc-200 bg-zinc-50/80 p-3 has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50">
                <input
                  type="checkbox"
                  checked={upsellBusinessPush}
                  onChange={(e) => setUpsellBusinessPush(e.target.checked)}
                  className="mt-1 size-5 accent-violet-600"
                />
                <span>
                  <span className="font-bold">{DO_OR_DUMP_UPSELL_BUSINESS_PUSH.label}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-zinc-600">
                    {DO_OR_DUMP_UPSELL_BUSINESS_PUSH.detail}
                  </span>
                  <span className="mt-1 block text-xs font-bold text-zinc-900">
                    Turnaround: {DO_OR_DUMP_UPSELL_BUSINESS_PUSH.turnaround}
                  </span>
                  <span className="mt-1 block text-sm font-black text-violet-700">
                    ${DO_OR_DUMP_UPSELL_BUSINESS_PUSH.amountUsd.toFixed(2)}
                  </span>
                </span>
              </label>
            </div>
            <div className="mt-4 flex flex-col gap-2 border-t border-zinc-200 pt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-600">Due today</span>
                <span className="font-black">${upsellTotal.toFixed(2)}</span>
              </div>
              <button
                type="button"
                disabled={upsellTotal <= 0 || checkoutLoading}
                onClick={() => void handleCheckout()}
                className="min-h-12 w-full rounded-xl bg-violet-600 px-4 py-3 text-sm font-bold text-white shadow-md transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {checkoutLoading ? "Opening checkout…" : upsellTotal > 0 ? `Pay securely — $${upsellTotal.toFixed(2)}` : "Select an add‑on"}
              </button>
              {checkoutError ? <p className="text-center text-sm text-rose-600">{checkoutError}</p> : null}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={`rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 ${wide ? "sm:col-span-2 lg:col-span-2" : ""}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-0.5 font-bold text-zinc-900">{value}</p>
    </div>
  );
}
